import type { AxiosInstance } from "axios";
import type {
  CreatedScheduleCard,
  CreateCardFromScheduleResult,
  ScheduleSearchItem,
  ScheduleSearchMeta,
  ScheduleSearchPerson,
  ScheduleSearchProperty,
  ScheduleSearchResult,
} from "../types/scheduleCard";
import { getApiErrorToastTitle } from "../utils/apiErrorToast";
import {
  buildScheduleSearchParams,
  canCreateCardFromSchedule,
  type ScheduleSearchParamsInput,
} from "../utils/scheduleSearchParams";

export type {
  CreatedScheduleCard,
  CreateCardFromScheduleResult,
  ScheduleSearchItem,
  ScheduleSearchMeta,
  ScheduleSearchPerson,
  ScheduleSearchProperty,
  ScheduleSearchResult,
};

function mapPerson(raw: unknown): ScheduleSearchPerson | null {
  if (!raw || typeof raw !== "object") return null;
  const person = raw as Record<string, unknown>;
  if (person.id == null) return null;
  return {
    id: Number(person.id),
    first_name: String(person.first_name ?? ""),
    email: typeof person.email === "string" ? person.email : null,
    phone: typeof person.phone === "string" ? person.phone : null,
  };
}

function mapProperty(raw: unknown): ScheduleSearchProperty | null {
  if (!raw || typeof raw !== "object") return null;
  const property = raw as Record<string, unknown>;
  if (property.id == null) return null;
  return {
    id: Number(property.id),
    title: String(property.title ?? ""),
    address:
      property.address && typeof property.address === "object"
        ? (property.address as Record<string, unknown>)
        : null,
  };
}

export function mapScheduleSearchItem(raw: unknown): ScheduleSearchItem | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  if (item.id == null) return null;

  const mapped = {
    id: Number(item.id),
    date: String(item.date ?? ""),
    status: Number(item.status),
    client: mapPerson(item.client),
    consultant: mapPerson(item.consultant),
    property: mapProperty(item.property),
    has_card: Boolean(item.has_card),
    card_id: item.card_id != null ? Number(item.card_id) : null,
    can_create_card: false,
  };

  return {
    ...mapped,
    can_create_card: canCreateCardFromSchedule({
      can_create_card:
        typeof item.can_create_card === "boolean" ? item.can_create_card : undefined,
      has_card: mapped.has_card,
      status: mapped.status,
    }),
  };
}

function extractSearchPayload(payload: unknown): {
  items: unknown[];
  meta: Partial<ScheduleSearchMeta>;
} {
  if (!payload || typeof payload !== "object") {
    return { items: [], meta: {} };
  }

  const root = payload as { data?: unknown };
  const data = (root.data ?? payload) as {
    items?: unknown;
    meta?: Partial<ScheduleSearchMeta>;
  };

  return {
    items: Array.isArray(data.items) ? data.items : [],
    meta: data.meta ?? {},
  };
}

export function markScheduleItemAsHavingCard(
  items: ScheduleSearchItem[],
  scheduleId: number,
  cardId: number | null
): ScheduleSearchItem[] {
  return items.map((item) =>
    item.id === scheduleId
      ? { ...item, has_card: true, can_create_card: false, card_id: cardId }
      : item
  );
}

export async function searchSchedules(
  client: AxiosInstance,
  input: ScheduleSearchParamsInput,
  today?: string
): Promise<ScheduleSearchResult> {
  const response = await client.get("/api/adm/schedules/search", {
    params: buildScheduleSearchParams(input, today),
  });

  const { items, meta } = extractSearchPayload(response.data);

  return {
    items: items
      .map(mapScheduleSearchItem)
      .filter((item): item is ScheduleSearchItem => item != null),
    meta: {
      current_page: Number(meta.current_page ?? input.page ?? 1) || 1,
      last_page: Number(meta.last_page ?? 1) || 1,
      per_page: Number(meta.per_page ?? input.perPage ?? 15) || 15,
      total: Number(meta.total ?? items.length) || 0,
    },
  };
}

export async function createCardFromSchedule(
  client: AxiosInstance,
  scheduleId: number
): Promise<CreateCardFromScheduleResult> {
  try {
    const response = await client.post("/api/adm/boards/cards-from-schedule", {
      schedule_id: scheduleId,
    });

    const payload = response.data as {
      data?: CreatedScheduleCard;
      message?: string;
    };

    return {
      ok: true,
      card: payload.data ?? { id: 0 },
      message: payload.message?.trim() || "Card criado com sucesso!",
    };
  } catch (error) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: {
          message?: unknown;
          data?: { card_id?: number | null; has_card?: boolean };
        };
      };
    };
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;

    if (status === 409) {
      return {
        ok: false,
        reason: "already_has_card",
        cardId: data?.data?.card_id != null ? Number(data.data.card_id) : null,
        message:
          typeof data?.message === "string" && data.message.trim()
            ? data.message.trim()
            : "Este agendamento já possui um card.",
      };
    }

    return {
      ok: false,
      reason: "error",
      status,
      message: getApiErrorToastTitle(error, "Não foi possível criar o card."),
    };
  }
}
